import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from '../contexts/LanguageContext';
import api, { getSettings } from '../services/api';

const AddItem = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [itemType, setItemType] = useState('dish');
  const [menus, setMenus] = useState([]);
  const [categories, setCategories] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    priceLBP: '',
    description: '',
    imageUrl: '',
    menuId: '',
    categoryId: ''
  });
  const [dollarRate, setDollarRate] = useState(89000);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editDishId, setEditDishId] = useState(null);

  useEffect(() => {
    fetchMenus();
    fetchSettings();
    
    // Handle navigation state from Dishes page
    if (location.state) {
      if (location.state.itemType) {
        setItemType(location.state.itemType);
      }
      if (location.state.editMode && location.state.dishData) {
        setEditMode(true);
        setEditDishId(location.state.dishData._id);
        
        // Calculate LBP price from USD price
        const usdPrice = parseFloat(location.state.dishData.price) || 0;
        const lbpPrice = usdPrice > 0 ? Math.round(usdPrice * dollarRate) : '';
        
        setFormData({
          name: location.state.dishData.name || '',
          price: location.state.dishData.price || '',
          priceLBP: lbpPrice,
          description: location.state.dishData.description || '',
          imageUrl: location.state.dishData.imageUrl || '',
          menuId: location.state.dishData.categoryId ? '' : location.state.dishData.menuId || '',
          categoryId: location.state.dishData.categoryId || ''
        });
        // If dish has categoryId, fetch categories for that menu
        if (location.state.dishData.categoryId) {
          // We need to find the menuId for this category
          fetchCategoriesForDish(location.state.dishData.categoryId);
        }
      }
      if (location.state.preselectedMenu) {
        setFormData(prev => ({
          ...prev,
          menuId: location.state.preselectedMenu
        }));
      }
      if (location.state.preselectedCategory) {
        setFormData(prev => ({
          ...prev,
          categoryId: location.state.preselectedCategory
        }));
      }
    }
  }, [location.state]);

  useEffect(() => {
    if (formData.menuId && itemType === 'dish') {
      fetchCategories(formData.menuId);
    }
  }, [formData.menuId, itemType]);

  const fetchMenus = async () => {
    try {
      const response = await api.get('/menus');
      setMenus(response.data);
    } catch (error) {
      console.error('Error fetching menus:', error);
    }
  };

  const fetchCategories = async (menuId) => {
    try {
      const response = await api.get(`/categories/${menuId}`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchCategoriesForDish = async (categoryId) => {
    try {
      // First, get all categories to find the one we're editing
      const allCategoriesResponse = await api.get('/categories');
      const targetCategory = allCategoriesResponse.data.find(cat => cat._id === categoryId);
      if (targetCategory && targetCategory.menuId) {
        setFormData(prev => ({
          ...prev,
          menuId: targetCategory.menuId,
          categoryId: categoryId
        }));
        fetchCategories(targetCategory.menuId);
      }
    } catch (error) {
      console.error('Error fetching categories for dish:', error);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await getSettings();
      if (response.data && response.data.dollarRate) {
        setDollarRate(parseFloat(response.data.dollarRate));
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'priceLBP') {
      // Calculate USD price from LBP
      const lbpValue = parseFloat(value) || 0;
      const usdPrice = lbpValue > 0 ? (lbpValue / dollarRate).toFixed(2) : '';
      
      setFormData(prev => ({
        ...prev,
        priceLBP: value,
        price: usdPrice
      }));
    } else if (name === 'price') {
      // Calculate LBP price from USD
      const usdValue = parseFloat(value) || 0;
      const lbpPrice = usdValue > 0 ? Math.round(usdValue * dollarRate) : '';
      
      setFormData(prev => ({
        ...prev,
        price: value,
        priceLBP: lbpPrice
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set dragActive to false if we're leaving the actual drop area
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragActive(false);
    }
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
    setMessage(''); // Clear any previous messages
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const files = e.dataTransfer.files;
    console.log('Files dropped:', files); // Debug log
    
    if (files && files.length > 0) {
      const file = files[0];
      console.log('File info:', file.name, file.type); // Debug log
      
      // Check if file is an image
      if (file.type.startsWith('image/')) {
        setSelectedFile(file);
        // Create preview URL for display
        const previewUrl = URL.createObjectURL(file);
        setFormData(prev => ({
          ...prev,
          imageUrl: previewUrl
        }));
        setMessage('Image selected successfully!');
      } else {
        setMessage('Please upload an image file (JPG, PNG, GIF, etc.)');
      }
    } else {
      setMessage('No files detected');
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    setMessage(''); // Clear any previous messages
    if (file) {
      if (file.type.startsWith('image/')) {
        setSelectedFile(file);
        // Create preview URL for display
        const previewUrl = URL.createObjectURL(file);
        setFormData(prev => ({
          ...prev,
          imageUrl: previewUrl
        }));
        setMessage('Image selected successfully!');
      } else {
        setMessage('Please upload an image file (JPG, PNG, GIF, etc.)');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      if (itemType === 'menu') {
        await api.post('/menus', { name: formData.name });
        setMessage(editMode ? 'Menu updated successfully!' : 'Menu added successfully!');
        setFormData({ name: '', price: '', priceLBP: '', description: '', imageUrl: '', menuId: '', categoryId: '' });
        fetchMenus();
      } else if (itemType === 'category') {
        if (editMode) {
          await api.put(`/categories/${editDishId}`, { 
            name: formData.name, 
            menuId: formData.menuId 
          });
          setMessage('Category updated successfully!');
        } else {
          await api.post('/categories', { 
            name: formData.name, 
            menuId: formData.menuId 
          });
          setMessage('Category added successfully!');
        }
        setFormData({ name: '', price: '', priceLBP: '', description: '', imageUrl: '', menuId: '', categoryId: '' });
      } else if (itemType === 'dish') {
        // Create FormData for file upload
        const formDataToSend = new FormData();
        formDataToSend.append('name', formData.name);
        formDataToSend.append('priceLBP', formData.priceLBP); // LBP price
        formDataToSend.append('price', formData.price); // USD price calculated from LBP
        formDataToSend.append('description', formData.description);
        formDataToSend.append('categoryId', formData.categoryId);
        
        // Only append image if it exists
        if (selectedFile) {
          formDataToSend.append('image', selectedFile);
        }

        let response;
        if (editMode) {
          response = await api.put(`/dishes/${editDishId}`, formDataToSend, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });
          setMessage('Dish updated successfully!');
        } else {
          response = await api.post('/dishes', formDataToSend, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });
          setMessage('Dish added successfully!');
        }
        
        setFormData({ name: '', price: '', priceLBP: '', description: '', imageUrl: '', menuId: '', categoryId: '' });
        setSelectedFile(null);
      }
    } catch (error) {
      setMessage(`Error ${editMode ? 'updating' : 'adding'} item. Please try again.`);
      console.error(`Error ${editMode ? 'updating' : 'adding'} item:`, error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="content">
      <h1 className="page-title">{editMode ? t('editItem') : t('addItem')}</h1>
      
      <div className="card">
        {!editMode && (
          <div className="add-item-tabs">
            <button 
              className={`tab-btn ${itemType === 'menu' ? 'active' : ''}`}
              onClick={() => setItemType('menu')}
            >
              {t('addMenu')}
            </button>
            <button 
              className={`tab-btn ${itemType === 'category' ? 'active' : ''}`}
              onClick={() => setItemType('category')}
            >
              {t('addCategory')}
            </button>
            <button 
              className={`tab-btn ${itemType === 'dish' ? 'active' : ''}`}
              onClick={() => setItemType('dish')}
            >
              {t('addNewDish')}
            </button>
          </div>
        )}

        {message && (
          <div className={`message ${message.includes('successfully') ? 'message-success' : 'message-error'}`}>
            {message}
          </div>
        )}

        <form onSubmit={handleSubmit} className="add-item-form">
          <div className="form-group">
            <label className="form-label">
              {editMode ? 
                (itemType === 'menu' ? t('menuName') : itemType === 'category' ? t('categoryName') : t('dishName')) :
                (itemType === 'menu' ? t('menuName') : itemType === 'category' ? t('categoryName') : t('dishName'))
              }
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className="form-input"
              required
            />
          </div>

          {!editMode && itemType === 'category' && (
            <div className="form-group">
              <label className="form-label">{t('selectMenu')}</label>
              <select
                name="menuId"
                value={formData.menuId}
                onChange={handleInputChange}
                className="form-select"
                required
              >
                <option value="">{t('choose')}</option>
                {menus.map(menu => (
                  <option key={menu._id} value={menu._id}>{menu.name}</option>
                ))}
              </select>
            </div>
          )}

          {itemType === 'dish' && (
            <>
              <div className="form-group">
                <label className="form-label">{t('selectMenu')}</label>
                <select
                  name="menuId"
                  value={formData.menuId}
                  onChange={handleInputChange}
                  className="form-select"
                  required
                >
                  <option value="">{t('choose')}</option>
                  {menus.map(menu => (
                    <option key={menu._id} value={menu._id}>{menu.name}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">{t('selectCategory')}</label>
                <select
                  name="categoryId"
                  value={formData.categoryId}
                  onChange={handleInputChange}
                  className="form-select"
                  required
                >
                  <option value="">{t('choose')}</option>
                  {categories.map(category => (
                    <option key={category._id} value={category._id}>{category.name}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">{t('dishPriceLBP') || 'Price in Lebanese Lira (LBP)'}</label>
                <input
                  type="number"
                  name="priceLBP"
                  value={formData.priceLBP}
                  onChange={handleInputChange}
                  className="form-input"
                  step="1000"
                  min="0"
                  placeholder="Enter price in LBP"
                  required
                />
                <small style={{ color: '#b0b0b0', marginTop: '5px', display: 'block' }}>
                  Rate: 1 USD = {dollarRate.toLocaleString()} LBP
                </small>
              </div>

              <div className="form-group">
                <label className="form-label">{t('dishPriceUSD') || 'Price in USD (Auto-calculated)'}</label>
                <input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleInputChange}
                  className="form-input"
                  step="0.01"
                  min="0"
                  placeholder="USD price will be calculated automatically"
                  required
                  readOnly
                  style={{ backgroundColor: '#f5f5f5' }}
                />
              </div>

              <div className="form-group">
                <label className="form-label">{t('dishDescription')}</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  className="form-input"
                  rows="3"
                />
              </div>

              <div className="form-group">
                <label className="form-label">{t('dishImage')}</label>
                <div 
                  className={`image-upload-area ${dragActive ? 'drag-active' : ''}`}
                  onDragEnter={handleDragEnter}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  {formData.imageUrl ? (
                    <div className="image-preview">
                      <img src={formData.imageUrl} alt="Preview" />
                      <button 
                        type="button"
                        className="remove-image-btn"
                        onClick={() => {
                          setFormData(prev => ({ ...prev, imageUrl: '' }));
                          setSelectedFile(null);
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <div className="upload-placeholder">
                      <div className="upload-icon">📷</div>
                      <div className="upload-text">
                        <p>{t('dragDropImage')}</p>
                        <p>or</p>
                        <button 
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => document.getElementById('file-input').click()}
                        >
                          {t('browseFiles')}
                        </button>
                      </div>
                    </div>
                  )}
                  <input
                    id="file-input"
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                  />
                </div>
              </div>
            </>
          )}

          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? t('loading') : (editMode ? `${t('update')} ${itemType.charAt(0).toUpperCase() + itemType.slice(1)}` : `${t('add')} ${itemType.charAt(0).toUpperCase() + itemType.slice(1)}`)}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AddItem;
